import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, PlanetName } from '../../types/kundali';
import {
  ConcernDomainId,
  ConcernDomainMeta,
  CANONICAL_CONCERN_DOMAINS,
  ConcernTimingWindow,
  EpistemicInsightStatement,
  ConcernUpayaPractice,
  EPISTEMIC_CLASSIFICATION_REGISTRY,
  VartmanConcernIntelligenceResult
} from '../../types/vartmanConcernIntelligence';
import {
  evaluateConcernIntelligence,
  evaluateFreeTextQuery
} from '../../utils/vartamanaSthitiEngine';
import {
  Moon,
  Briefcase,
  TrendingUp,
  Activity,
  Heart,
  Home,
  Compass,
  Search,
  X,
  AlertTriangle,
  Info,
  Calendar,
  Sparkles,
  Shield,
  Layers,
  Repeat,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock
} from 'lucide-react';

interface WhatsOnYourMindSectionProps {
  kundali: CompleteKundali;
  focusDate: Date | string;
  isAcharyaView?: boolean;
  lang?: Language;
}

export const WhatsOnYourMindSection: React.FC<WhatsOnYourMindSectionProps> = ({
  kundali,
  focusDate,
  isAcharyaView = false,
  lang = 'en'
}) => {
  const isHi = lang === 'hi';
  const [selectedDomain, setSelectedDomain] = useState<ConcernDomainId>('INNER_WELLBEING');
  const [freeTextInput, setFreeTextInput] = useState<string>('');
  const [activeQuery, setActiveQuery] = useState<string>('');

  // Evaluate Concern Intelligence for active domain and optional query
  const result: VartmanConcernIntelligenceResult = useMemo(() => {
    return evaluateConcernIntelligence(
      kundali,
      focusDate,
      selectedDomain,
      activeQuery.trim().length > 0 ? activeQuery : undefined,
      lang
    );
  }, [kundali, focusDate, selectedDomain, activeQuery, lang]);

  const handleQuerySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!freeTextInput.trim()) {
      setActiveQuery('');
      return;
    }
    const evalRes = evaluateFreeTextQuery(freeTextInput, lang);
    setActiveQuery(freeTextInput.trim());
    if (evalRes.matchedDomain) {
      setSelectedDomain(evalRes.matchedDomain);
    }
  };

  const handleClearQuery = () => {
    setFreeTextInput('');
    setActiveQuery('');
  };

  const domainMeta = CANONICAL_CONCERN_DOMAINS[selectedDomain] || CANONICAL_CONCERN_DOMAINS.INNER_WELLBEING;

  // Domain icon helper
  const renderDomainIcon = (iconName: string, className: string = 'w-4 h-4') => {
    switch (iconName) {
      case 'Moon': return <Moon className={className} />;
      case 'Briefcase': return <Briefcase className={className} />;
      case 'TrendingUp': return <TrendingUp className={className} />;
      case 'Activity': return <Activity className={className} />;
      case 'Heart': return <Heart className={className} />;
      case 'Home': return <Home className={className} />;
      case 'Compass': return <Compass className={className} />;
      default: return <Compass className={className} />;
    }
  };

  return (
    <section
      aria-labelledby="whats-on-your-mind-heading"
      className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-6"
    >
      {/* 1. Header & Title */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border/70 pb-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-astro-accent text-white text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              {isHi ? 'वर्तमान संशय' : 'Concern Intelligence'}
            </span>
            <span className="text-[11px] px-2 py-0.5 rounded-md bg-astro-bg border border-astro-border text-astro-secondary-text font-semibold">
              {isHi ? 'गैर-नियतिवादी फलित' : 'Non-Fatalistic Epistemic Model'}
            </span>
          </div>

          <h2
            id="whats-on-your-mind-heading"
            className="text-lg sm:text-xl font-bold font-serif text-astro-primary mt-1.5"
          >
            {isHi ? 'आपके मन में क्या विचार है? (What’s on Your Mind?)' : 'What’s on Your Mind? (Explore Life Concerns)'}
          </h2>
          <p className="text-xs text-astro-secondary-text mt-1 max-w-3xl leading-relaxed">
            {isHi
              ? 'विशिष्ट जीवन-क्षेत्रों का शास्त्रीय ज्योतिषीय साक्ष्यों के आधार पर अन्वेषण करें। कोई पूर्वधारणा या चिकित्सा-संबंधी दावा किए बिना केवल वस्तुनिष्ठ ग्रहीय कालखंड प्रस्तुत किए जाते हैं।'
              : 'Inquire into specific life areas grounded in classical Jyotish evidence without fear-based or deterministic assumptions. Planetary rhythms provide contemplative temporal frameworks rather than biological or deterministic causes.'}
          </p>
        </div>
      </div>

      {/* 2. Free-Text Search Input */}
      <form onSubmit={handleQuerySubmit} className="space-y-2">
        <div className="relative flex items-center">
          <Search className="w-4 h-4 text-astro-secondary-text absolute left-3 pointer-events-none" />
          <input
            type="text"
            value={freeTextInput}
            onChange={(e) => setFreeTextInput(e.target.value)}
            placeholder={
              isHi
                ? 'कोई विचार या चिंता लिखें (उदा. करियर बदलाव, मानसिक शांति, आर्थिक स्थिति, संबंध)...'
                : 'Type a thought, feeling, or concern (e.g. career growth, inner peace, finances, family relocation)...'
            }
            className="w-full pl-9 pr-24 py-2 text-xs bg-astro-bg border border-astro-border rounded-xl text-astro-text placeholder-astro-secondary-text focus:outline-hidden focus:ring-2 focus:ring-astro-accent focus:border-astro-accent transition-all"
            aria-label={isHi ? 'संशय खोजें' : 'Search life concern'}
          />
          <div className="absolute right-1.5 flex items-center gap-1">
            {freeTextInput && (
              <button
                type="button"
                onClick={handleClearQuery}
                className="p-1 text-astro-secondary-text hover:text-astro-text rounded-md transition-colors"
                title={isHi ? 'साफ करें' : 'Clear'}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="submit"
              className="px-3 py-1 bg-astro-accent hover:bg-astro-accent-hover text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer"
            >
              {isHi ? 'अन्वेषण' : 'Explore'}
            </button>
          </div>
        </div>

        {/* Free-text Query Evaluation Feedback */}
        {result.freeTextEvaluation && result.freeTextEvaluation.sanitizedQuery && (
          <div className="flex items-center gap-2 text-[11px] px-3 py-1.5 bg-astro-bg rounded-lg border border-astro-border text-astro-secondary-text">
            <Info className="w-3.5 h-3.5 text-astro-accent shrink-0" />
            <span>
              {isHi ? 'संबद्ध खोज:' : 'Interpreted inquiry:'}{' '}
              <span className="font-semibold text-astro-primary font-mono">
                "{result.freeTextEvaluation.sanitizedQuery}"
              </span>
            </span>
            {result.freeTextEvaluation.matchedDomain && (
              <span className="ml-auto px-2 py-0.5 rounded text-[10px] bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 font-bold">
                {isHi ? 'संरेखित:' : 'Mapped to:'}{' '}
                {isHi
                  ? CANONICAL_CONCERN_DOMAINS[result.freeTextEvaluation.matchedDomain].labelHi
                  : CANONICAL_CONCERN_DOMAINS[result.freeTextEvaluation.matchedDomain].labelEn}
              </span>
            )}
            {result.freeTextEvaluation.clarificationPromptEn && (
              <span className="text-[10px] text-astro-secondary-text italic ml-2">
                {isHi ? result.freeTextEvaluation.clarificationPromptHi : result.freeTextEvaluation.clarificationPromptEn}
              </span>
            )}
          </div>
        )}
      </form>

      {/* 3. 7 Canonical Concern Domain Chips */}
      <div className="space-y-2">
        <label className="text-[11px] font-bold text-astro-primary uppercase tracking-wider block">
          {isHi ? 'जीवन क्षेत्र चयन (७ प्रामाणिक क्षेत्र)' : 'Select Life Domain (7 Canonical Domains)'}
        </label>
        <div
          role="tablist"
          aria-label={isHi ? 'जीवन क्षेत्र' : 'Life Domains'}
          className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2"
        >
          {(Object.keys(CANONICAL_CONCERN_DOMAINS) as ConcernDomainId[]).map((domId) => {
            const d = CANONICAL_CONCERN_DOMAINS[domId];
            const isSelected = selectedDomain === domId;
            return (
              <button
                key={domId}
                role="tab"
                aria-selected={isSelected}
                tabIndex={isSelected ? 0 : -1}
                onClick={() => {
                  setSelectedDomain(domId);
                  setActiveQuery('');
                  setFreeTextInput('');
                }}
                className={`flex flex-col items-center text-center p-2.5 rounded-xl border transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-astro-primary text-white border-astro-primary shadow-xs ring-2 ring-astro-accent/40 font-bold'
                    : 'bg-white dark:bg-astro-bg text-astro-secondary-text border-astro-border hover:border-astro-accent/50 hover:text-astro-primary'
                }`}
              >
                <div className={`p-1.5 rounded-lg mb-1.5 ${isSelected ? 'bg-white/20 text-white' : 'bg-astro-bg text-astro-primary'}`}>
                  {renderDomainIcon(d.iconName, 'w-4 h-4')}
                </div>
                <span className="text-[11px] font-semibold leading-tight line-clamp-1">
                  {isHi ? d.labelHi : d.labelEn}
                </span>
                <span className={`text-[9px] mt-0.5 line-clamp-1 ${isSelected ? 'text-white/80' : 'text-astro-secondary-text'}`}>
                  {d.sanskritName.split(' ')[0]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 4. Active Domain Summary Banner */}
      <div className="p-3.5 bg-astro-bg rounded-xl border border-astro-border flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-bold text-astro-primary text-sm font-serif">
              {isHi ? domainMeta.labelHi : domainMeta.labelEn}
            </span>
            <span className="text-[11px] px-2 py-0.5 rounded bg-white dark:bg-astro-card border border-astro-border font-mono text-astro-accent font-semibold">
              {domainMeta.sanskritName}
            </span>
          </div>
          <p className="text-[11px] text-astro-secondary-text max-w-2xl">
            {isHi ? domainMeta.descriptionHi : domainMeta.descriptionEn}
          </p>
        </div>

        <div className="flex items-center gap-4 shrink-0 text-[11px] border-t md:border-t-0 md:border-l border-astro-border/70 pt-2 md:pt-0 md:pl-4">
          <div>
            <span className="text-astro-secondary-text block">{isHi ? 'प्राथमिक भाव:' : 'Primary Houses:'}</span>
            <span className="font-mono font-bold text-astro-primary">
              {domainMeta.primaryHouses.map(h => `H${h}`).join(', ')}
            </span>
          </div>
          <div>
            <span className="text-astro-secondary-text block">{isHi ? 'कारक ग्रह:' : 'Karakas:'}</span>
            <span className="font-mono font-bold text-astro-accent">
              {domainMeta.karakas.join(', ')}
            </span>
          </div>
        </div>
      </div>

      {/* 5. Multi-Window Timing Display (Strict Midpoint Barred; Multi/Repeated/Reversed Events) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-astro-accent" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-primary">
              {isHi ? 'समय कालखंड एवं गोचर उत्प्रेरक (बहु-विंडो विश्लेषण)' : 'Timing Horizons & Event Windows (Multi-Window Resolution)'}
            </h3>
          </div>
          <span className="text-[10px] text-astro-secondary-text italic">
            {isHi ? 'मध्यबिंदु स्वतः अंगीकरण निषिद्ध' : 'Arithmetic midpoint auto-adoption prohibited'}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {result.timingWindows.map((win, idx) => {
            const isPlateau = win.peakDate === null;
            return (
              <div
                key={win.windowId || idx}
                className="p-3.5 bg-white dark:bg-astro-bg rounded-xl border border-astro-border space-y-2 flex flex-col justify-between"
              >
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-1 flex-wrap">
                    <span className="text-[10px] px-2 py-0.5 rounded font-mono font-bold bg-astro-secondary-surface text-astro-primary">
                      {isHi ? win.titleHi : win.titleEn}
                    </span>
                    {win.isReversedOrRepeated && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-300 border border-purple-300 font-bold flex items-center gap-1">
                        <Repeat className="w-2.5 h-2.5" />
                        {isHi ? 'पुनरावर्ती' : 'Repeated / Return'}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 text-xs font-semibold text-astro-primary">
                    <Calendar className="w-3.5 h-3.5 text-astro-secondary-text" />
                    <span>{win.durationLabelEn}</span>
                  </div>

                  {/* Peak Date vs Continuous Plateau Indicator */}
                  <div className="pt-1">
                    {isPlateau ? (
                      <div className="p-1.5 rounded bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/60 text-[10px] text-purple-900 dark:text-purple-300">
                        <span className="font-bold block">
                          {isHi ? 'निरंतर स्थिर कालखंड (कोई कृत्रिम मध्यबिंदु नहीं)' : 'Continuous Plateau (No Midpoint Auto-Adopted)'}
                        </span>
                        <p className="text-[9px] text-purple-800/80 dark:text-purple-300/80 mt-0.5">
                          {isHi ? win.peakDateRationaleHi : win.peakDateRationaleEn}
                        </p>
                      </div>
                    ) : (
                      <div className="p-1.5 rounded bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/60 text-[10px] text-emerald-900 dark:text-emerald-300">
                        <span className="font-bold block">
                          {isHi ? `सटीक खगोलीय गोचर: ${win.peakDate}` : `Exact Astronomical Transit: ${win.peakDate}`}
                        </span>
                        <p className="text-[9px] text-emerald-800/80 dark:text-emerald-300/80 mt-0.5">
                          {isHi ? win.peakDateRationaleHi : win.peakDateRationaleEn}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-2 border-t border-astro-border/50 text-[10px] space-y-1">
                  <div>
                    <span className="text-astro-secondary-text">{isHi ? 'निर्धारक कारक:' : 'Determining Factor:'} </span>
                    <span className="font-medium text-astro-primary">
                      {isHi ? win.determiningAstrologicalFactorHi : win.determiningAstrologicalFactorEn}
                    </span>
                  </div>
                  <p className="text-astro-secondary-text italic text-[9px]">
                    {isHi ? win.significanceHi : win.significanceEn}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 6. 6-Tier Epistemic Insight Statements (100% Categorized) */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-astro-primary" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-astro-primary">
            {isHi ? '६-स्तरीय शास्त्रीय फलित साक्ष्य (Epistemic Evidence)' : '6-Tier Epistemic Evidence Statements (100% Categorized)'}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {result.epistemicInsights.map((insight) => {
            const badgeCfg = EPISTEMIC_CLASSIFICATION_REGISTRY[insight.category];
            return (
              <div
                key={insight.id}
                className="p-3 bg-white dark:bg-astro-bg rounded-xl border border-astro-border space-y-1.5 text-xs"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold border ${badgeCfg.colorClass}`}>
                    {insight.labelTag} {isHi ? badgeCfg.badgeLabelHi : badgeCfg.badgeLabelEn}
                  </span>
                  {insight.shastraReference && (
                    <span className="text-[9px] text-astro-secondary-text font-serif italic flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      {insight.shastraReference}
                    </span>
                  )}
                </div>

                <h4 className="font-bold text-astro-primary text-xs">
                  {isHi ? insight.titleHi : insight.titleEn}
                </h4>

                <p className="text-[11px] text-astro-text leading-relaxed">
                  {isHi ? insight.narrativeHi : insight.narrativeEn}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* 7. Psychological Safety Banner & Upaya Grounding Practices */}
      <div className="space-y-3">
        {/* Mandatory Medical & Non-Causation Disclaimer */}
        <div className="p-3.5 bg-amber-50/70 dark:bg-amber-950/30 rounded-xl border border-amber-300 dark:border-amber-800 space-y-1.5 text-xs text-amber-950 dark:text-amber-200">
          <div className="flex items-center gap-2 font-bold text-amber-900 dark:text-amber-300">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
            <span>
              {isHi ? 'चिकित्सकीय एवं मनोवैज्ञानिक परामर्श सूचना (Mandatory Safety Disclaimer)' : 'Psychological Safety & Medical Disclaimer'}
            </span>
          </div>
          <p className="text-[11px] leading-relaxed">
            {isHi ? result.safetyFraming.medicalDisclaimerHi : result.safetyFraming.medicalDisclaimerEn}
          </p>
          <p className="text-[10px] text-amber-900/80 dark:text-amber-300/80 italic pt-0.5">
            {isHi ? result.safetyFraming.nonCausationStatementHi : result.safetyFraming.nonCausationStatementEn}
          </p>
        </div>

        {/* Upaya Grounding Practices */}
        <div className="space-y-2">
          <label className="text-[11px] font-bold text-astro-primary uppercase tracking-wider block">
            {isHi ? 'पारंपरिक संतुलन अभ्यास (उपाय — आत्म-संतुलन हेतु)' : 'Traditional Grounding Practices (Upayas — Non-Guaranteed)'}
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {result.upayas.map((upaya) => (
              <div
                key={upaya.id}
                className="p-3 bg-white dark:bg-astro-bg rounded-xl border border-astro-border space-y-1.5 text-xs"
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="font-bold text-astro-primary text-[11px]">
                    {isHi ? upaya.titleHi : upaya.titleEn}
                  </span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-astro-secondary-surface text-astro-secondary-text font-mono font-semibold">
                    {upaya.category.replace('_', ' ')}
                  </span>
                </div>

                <p className="text-[11px] text-astro-text leading-relaxed">
                  {isHi ? upaya.practiceHi : upaya.practiceEn}
                </p>

                <div className="pt-1.5 border-t border-astro-border/50 text-[10px] flex items-center justify-between text-astro-secondary-text">
                  <span>{isHi ? 'उपयुक्त काल:' : 'Beneficial Window:'} {isHi ? upaya.beneficialPeriodHi : upaya.beneficialPeriodEn}</span>
                </div>
                <p className="text-[9px] text-astro-secondary-text italic leading-tight pt-0.5">
                  {isHi ? upaya.nonGuaranteeNoticeHi : upaya.nonGuaranteeNoticeEn}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 8. Acharya Deep-Dive Telemetry Section (Visible in Acharya Mode) */}
      {isAcharyaView && result.acharyaTelemetry && (
        <div className="p-4 bg-astro-bg rounded-xl border border-astro-border space-y-3 text-xs">
          <div className="flex items-center justify-between border-b border-astro-border pb-2">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-astro-primary" />
              <h4 className="font-bold text-astro-primary text-xs uppercase tracking-wider">
                {isHi ? 'आचार्य गहन विश्लेषण (Acharya Deep-Dive Telemetry)' : 'Acharya Deep-Dive Telemetry (Calculation Provenance)'}
              </h4>
            </div>
            <span className="text-[10px] font-mono px-2 py-0.5 bg-astro-primary text-white rounded font-bold">
              GOV-066 CERTIFIED
            </span>
          </div>

          {/* Natal Bhavas Table */}
          <div className="space-y-1.5">
            <span className="text-[11px] font-bold text-astro-primary block">
              {isHi ? '१. संबंधित जन्म भाव एवं अष्टकवर्ग रेखाएं' : '1. Evaluated Natal Bhavas & Sarvashtakavarga (SAV) Bindus'}
            </span>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] text-left border-collapse">
                <thead>
                  <tr className="border-b border-astro-border text-astro-secondary-text font-semibold">
                    <th className="py-1 px-2">{isHi ? 'भाव' : 'House'}</th>
                    <th className="py-1 px-2">{isHi ? 'राशि' : 'Sign'}</th>
                    <th className="py-1 px-2">{isHi ? 'भावेश' : 'Lord'}</th>
                    <th className="py-1 px-2">{isHi ? 'स्थित ग्रह' : 'Occupants'}</th>
                    <th className="py-1 px-2">{isHi ? 'दृष्टिकर्ता ग्रह' : 'Drishti Lords'}</th>
                    <th className="py-1 px-2">{isHi ? 'अष्टकवर्ग रेखाएं' : 'SAV Bindus'}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.acharyaTelemetry.natalBhavasEvaluated.map((b) => (
                    <tr key={b.house} className="border-b border-astro-border/50">
                      <td className="py-1 px-2 font-mono font-bold">House {b.house}</td>
                      <td className="py-1 px-2">{b.sign}</td>
                      <td className="py-1 px-2 font-semibold text-astro-accent">{b.lord}</td>
                      <td className="py-1 px-2">{b.occupants.length > 0 ? b.occupants.join(', ') : '—'}</td>
                      <td className="py-1 px-2">{b.drishtiLords.length > 0 ? b.drishtiLords.join(', ') : '—'}</td>
                      <td className="py-1 px-2 font-mono font-bold">
                        <span className={`px-1.5 py-0.2 rounded ${b.ashtakavargaBindus >= 28 ? 'text-emerald-700 bg-emerald-100 dark:bg-emerald-950/40' : 'text-amber-700 bg-amber-100 dark:bg-amber-950/40'}`}>
                          {b.ashtakavargaBindus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dasha State Dignity */}
          <div className="pt-2 border-t border-astro-border/50 text-[11px] space-y-1">
            <span className="font-bold text-astro-primary block">
              {isHi ? '२. ५-स्तरीय दशा गरिमा' : '2. Active Dasha Planetary Dignities'}
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 font-mono text-[10px]">
              <div className="p-2 bg-white dark:bg-astro-card rounded border border-astro-border">
                <span className="text-astro-secondary-text block">Mahadasha</span>
                <span className="font-bold text-astro-primary">{result.acharyaTelemetry.dashaState.mahaLord}</span>
                <span className="text-astro-accent ml-2">({result.acharyaTelemetry.dashaState.mahaDignity})</span>
              </div>
              <div className="p-2 bg-white dark:bg-astro-card rounded border border-astro-border">
                <span className="text-astro-secondary-text block">Antardasha</span>
                <span className="font-bold text-astro-primary">{result.acharyaTelemetry.dashaState.antarLord}</span>
                <span className="text-astro-accent ml-2">({result.acharyaTelemetry.dashaState.antarDignity})</span>
              </div>
              <div className="p-2 bg-white dark:bg-astro-card rounded border border-astro-border">
                <span className="text-astro-secondary-text block">Pratyantardasha</span>
                <span className="font-bold text-astro-primary">{result.acharyaTelemetry.dashaState.pratyantarLord}</span>
                <span className="text-astro-accent ml-2">({result.acharyaTelemetry.dashaState.pratyantarDignity})</span>
              </div>
            </div>
          </div>

          {/* Classical Sutras Cited */}
          {result.acharyaTelemetry.classicalSutrasCited.length > 0 && (
            <div className="pt-2 border-t border-astro-border/50 text-[10px] text-astro-secondary-text">
              <span className="font-bold text-astro-primary block mb-1">
                {isHi ? '३. उद्धृत संस्कृत सूत्र:' : '3. Classical Shastric Sutras Cited:'}
              </span>
              <ul className="list-disc list-inside space-y-0.5">
                {result.acharyaTelemetry.classicalSutrasCited.map((sutra, idx) => (
                  <li key={idx} className="font-serif italic">{sutra}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
export default WhatsOnYourMindSection;

